import { z } from 'zod';

import { config } from '@/config/config';
import { googleCallbackPath } from '@/modules/auth/googleCallbackUrl';
import {
  readGoogleCallbackParams,
  readIdTokenNonce,
} from '@/modules/auth/googleIdToken';
import type { OAuthSignInRequest } from '@/modules/auth/oauthCallbackFailure';
import type { AuthIntent } from '@/modules/auth/types/externalLogin';
import { sanitizeReturnTo } from '@/modules/auth/utils/sanitizeReturnTo';

/**
 * Google sign-in by full-page redirect. Google's own button signs in through a
 * popup that hands the token back over `window.opener`; iOS Safari drops that
 * link mid sign-in, leaving the popup blank on `accounts.google.com/gsi/transform`.
 * A redirect needs no opener. Google returns an ID token in the callback's
 * fragment, which the API's external login verifies as before. Slack and
 * LinkedIn go through oidc-client-ts, which supports only the code flow; the
 * API's external login takes Google's ID token, so state and nonce are kept
 * here instead.
 */
const googleAuthorizeEndpoint = 'https://accounts.google.com/o/oauth2/v2/auth';
const googleScope = 'openid email profile';
const storageKeyPrefix = 'googleSignIn:';

/**
 * Where Google sends the user back. Each origin's URI must be listed under the
 * OAuth client's authorized redirect URIs in Google Cloud Console.
 */
const getGoogleRedirectUri = () =>
  `${window.location.origin}${googleCallbackPath}`;

const storedSignInSchema = z.object({
  nonce: z.string(),
  returnTo: z.string(),
  // Google's redirect drops the page the user clicked on, so whether its
  // Terms Notice was shown and which page started the flow are kept here.
  acceptTerms: z.boolean(),
  intent: z.enum(['signUp', 'signIn']),
});

type StoredSignIn = z.infer<typeof storedSignInSchema>;

export type GoogleSsoErrorCode =
  | 'cancelled'
  | 'callback_error'
  | 'missing_state'
  | 'invalid_state'
  | 'missing_id_token'
  | 'invalid_nonce';

export class GoogleSsoError extends Error {
  public readonly code: GoogleSsoErrorCode;

  constructor(code: GoogleSsoErrorCode, message: string) {
    super(message);
    this.code = code;
  }
}

interface CompleteGoogleLoginResult {
  idToken: string;
  returnTo: string;
  acceptTerms: boolean;
  intent: AuthIntent;
}

const randomToken = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
    '',
  );
};

const readStoredSignIn = (state: string): StoredSignIn | undefined => {
  const raw = window.sessionStorage.getItem(`${storageKeyPrefix}${state}`);
  if (!raw) {
    return undefined;
  }

  try {
    const parsed = storedSignInSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
};

/** Forgets every started Google sign-in, so a callback can't be replayed. */
export const clearGoogleSigninState = () => {
  const keys = Array.from(
    { length: window.sessionStorage.length },
    (_, index) => window.sessionStorage.key(index),
  );
  for (const key of keys) {
    if (key?.startsWith(storageKeyPrefix)) {
      window.sessionStorage.removeItem(key);
    }
  }
};

export const startGoogleLogin = (
  returnTo: string | undefined,
  acceptTerms: boolean,
  intent: AuthIntent,
) => {
  clearGoogleSigninState();

  const state = randomToken();
  const stored: StoredSignIn = {
    nonce: randomToken(),
    returnTo: sanitizeReturnTo(returnTo),
    acceptTerms,
    intent,
  };
  window.sessionStorage.setItem(
    `${storageKeyPrefix}${state}`,
    JSON.stringify(stored),
  );

  const authorizeUrl = new URL(googleAuthorizeEndpoint);
  authorizeUrl.search = new URLSearchParams({
    client_id: config.external.googleClientId,
    redirect_uri: getGoogleRedirectUri(),
    response_type: 'id_token',
    scope: googleScope,
    nonce: stored.nonce,
    state,
    // Lets someone signed in to several Google accounts pick one, as Google's
    // own button did.
    prompt: 'select_account',
  }).toString();

  window.location.assign(authorizeUrl.toString());
};

export const completeGoogleLoginFromCallback = (
  callbackUrl: string,
): CompleteGoogleLoginResult => {
  const { idToken, state, error } = readGoogleCallbackParams(callbackUrl);

  if (error === 'access_denied') {
    throw new GoogleSsoError('cancelled', 'You cancelled the Google sign-in.');
  }
  if (error) {
    throw new GoogleSsoError(
      'callback_error',
      'Google sign-in failed. Please try again.',
    );
  }

  if (!state) {
    throw new GoogleSsoError(
      'missing_state',
      'Missing state in Google callback.',
    );
  }

  const stored = readStoredSignIn(state);
  if (!stored) {
    throw new GoogleSsoError(
      'invalid_state',
      'Google callback state could not be validated.',
    );
  }

  if (!idToken) {
    throw new GoogleSsoError(
      'missing_id_token',
      'Missing ID token in Google callback.',
    );
  }

  if (readIdTokenNonce(idToken) !== stored.nonce) {
    throw new GoogleSsoError(
      'invalid_nonce',
      'Google callback token could not be validated.',
    );
  }

  return {
    idToken,
    returnTo: sanitizeReturnTo(stored.returnTo),
    acceptTerms: stored.acceptTerms,
    intent: stored.intent,
  };
};

/**
 * The sign-in the callback answers, read without validating it, so a failed
 * one (e.g. the user cancelled at Google) can still be reported as a failed
 * sign-up or sign-in and restarted. Undefined when the stored state is gone.
 */
export const readGoogleSignInRequest = (
  callbackUrl: string,
): OAuthSignInRequest | undefined => {
  const { state } = readGoogleCallbackParams(callbackUrl);
  const stored = state ? readStoredSignIn(state) : undefined;
  return (
    stored && {
      returnTo: stored.returnTo,
      acceptTerms: stored.acceptTerms,
      intent: stored.intent,
    }
  );
};
